import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Button, IconButton, Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import React, { Fragment, useState } from "react";
import MultiQueryInstructionMenu from "./MultiQueryLabelMenu";

interface MultiQuerySearchBarProps {
  labelToRenderFunctionMap: Record<string, () => JSX.Element>;
  initialLabel: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    multiQuerySearchBarContainer: {
      display: "flex",
      alignItems: "flex-end",
    },
    searchType: {
      paddingLeft: theme.spacing(1),
    },
    searchBar: {
      flexGrow: 1,
    },
    iconButton: {
      padding: theme.spacing(1),
    },
    labelButtonsDiv: {
      display: "flex",
      flexDirection: "column",
      marginLeft: theme.spacing(),
    },
  })
);

export const MultiQuerySearchBar = ({ labelToRenderFunctionMap, initialLabel }: MultiQuerySearchBarProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string>(initialLabel);
  const queryLabels = Object.keys(labelToRenderFunctionMap);

  const searchBar = labelToRenderFunctionMap[selectedLabel]();

  const renderLabelButtons = () => {
    const lastIndex = queryLabels.length - 1;
    return queryLabels.map((label, index) => {
      const isTopButton = index === 0;
      const isBottomButton = index === lastIndex;
      return (
        <Button
          sx={[
            {
              m: 0,
              py: 0,
              px: 1,
              width: "100%",
              fontSize: "0.675rem",
              borderRadius: 0,
              color: selectedLabel === label ? "background.default" : "text.primary",
              backgroundColor: selectedLabel === label ? "text.primary" : "transparent",
            },
            isTopButton && {
              borderBottom: "none",
              borderTopLeftRadius: "4px",
              borderTopRightRadius: "4px",
              "&:hover": {
                borderBottom: "none",
              },
            },
            isBottomButton && {
              borderTop: "none",
              borderBottomLeftRadius: "4px",
              borderBottomRightRadius: "4px",
              "&:hover": {
                borderTop: "none",
              },
            },
          ]}
          variant={selectedLabel === label ? "contained" : "outlined"}
          onClick={() => setSelectedLabel(label)}
          key={`button-label-${label}`}
        >
          {label}
        </Button>
      );
    });
  };

  return (
    <div className={classes.multiQuerySearchBarContainer}>
      <div className={classes.searchBar}>{searchBar}</div>
      {queryLabels.length >= 2 ? (
        <div className={classes.labelButtonsDiv}>{renderLabelButtons()}</div>
      ) : (
        <Fragment>
          <div className={classes.searchType}>
            <IconButton
              aria-label="Search Type"
              className={classes.iconButton}
              onClick={e => setAnchorEl(e.currentTarget)}
              size="large">
              <MoreVertIcon fontSize="large" />
            </IconButton>
          </div>
          <MultiQueryInstructionMenu<string>
            queryLabels={queryLabels}
            selectedLabel={selectedLabel}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            onSelect={setSelectedLabel}
          />
        </Fragment>
      )}
    </div>
  );
};

export default MultiQuerySearchBar;
